import { RoomTemplate, solidWalls } from '../../sceneKit/roomTemplate'

export type RoomPresetId = 'bedroom' | 'studio' | 'living'

export type FurnitureKind = 'bed' | 'desk' | 'sofa' | 'shelf' | 'lamp' | 'rug' | 'plant' | 'coffeeTable' | 'easel'

export interface FurnitureSeed {
  kind: FurnitureKind
  x: number
  z: number
  rotY: number
}

export interface RoomPreset {
  label: string
  blurb: string
  template: RoomTemplate
  ambientColor: string
  ambientGround: string
  furniture: FurnitureSeed[]
}

export const ROOM_PRESETS: Record<RoomPresetId, RoomPreset> = {
  bedroom: {
    label: 'Bedroom',
    blurb: 'A quiet room to wake up in — bed against the back wall, a shelf and a plant in the corner.',
    template: { width: 7, height: 4.4, depth: 8, floorColor: '#372b30', ceilColor: '#0e0c12', walls: solidWalls('#5b4a52') },
    ambientColor: '#e8cdd6',
    ambientGround: '#2c2226',
    furniture: [
      { kind: 'bed', x: 0, z: 2.6, rotY: Math.PI },
      { kind: 'rug', x: 0, z: 0.6, rotY: 0 },
      { kind: 'shelf', x: -2.9, z: -2.6, rotY: Math.PI / 2 },
      { kind: 'lamp', x: 2.6, z: 2.6, rotY: 0 },
      { kind: 'plant', x: 2.7, z: -2.7, rotY: 0 },
    ],
  },
  studio: {
    label: 'Studio loft',
    blurb: 'A working space for making things — desk, easel, and a shelf for supplies.',
    template: { width: 10, height: 5.2, depth: 10, floorColor: '#332821', ceilColor: '#0e0c12', walls: solidWalls('#4a4038') },
    ambientColor: '#e0c9a6',
    ambientGround: '#3a2f24',
    furniture: [
      { kind: 'desk', x: -2.4, z: 3.2, rotY: Math.PI },
      { kind: 'easel', x: 1.8, z: 2, rotY: -0.4 },
      { kind: 'shelf', x: -4.4, z: -3, rotY: Math.PI / 2 },
      { kind: 'rug', x: 0, z: 0, rotY: 0 },
      { kind: 'plant', x: 4, z: -3.6, rotY: 0 },
    ],
  },
  living: {
    label: 'Living room',
    blurb: 'The main room of a house — sofa facing the wall of photos, a coffee table between.',
    template: { width: 11, height: 4.8, depth: 10, floorColor: '#26312c', ceilColor: '#0e0c12', walls: solidWalls('#3d4a44') },
    ambientColor: '#cfe0d6',
    ambientGround: '#212b26',
    furniture: [
      { kind: 'sofa', x: 0, z: 1.4, rotY: Math.PI },
      { kind: 'coffeeTable', x: 0, z: 3.2, rotY: 0 },
      { kind: 'shelf', x: -4.7, z: -3.4, rotY: Math.PI / 2 },
      { kind: 'lamp', x: 4.4, z: 0.5, rotY: 0 },
      { kind: 'plant', x: -4.6, z: 3.6, rotY: 0 },
      { kind: 'rug', x: 0, z: 1.8, rotY: 0 },
    ],
  },
}

export type FurnitureSource = 'primitive'

export interface FurnitureCatalogEntry {
  kind: FurnitureKind
  label: string
  /** How this catalog entry is rendered — today only `buildFurnitureMesh`'s hand-built primitives exist. A future `'model'` entry (with a `modelUrl`) can slot in later without touching placement/drag/rotate code, which only cares about a `TransformNode` + footprint. */
  source: FurnitureSource
}

export const FURNITURE_CATALOG: FurnitureCatalogEntry[] = [
  { kind: 'bed', label: 'Bed', source: 'primitive' },
  { kind: 'sofa', label: 'Sofa', source: 'primitive' },
  { kind: 'desk', label: 'Desk', source: 'primitive' },
  { kind: 'coffeeTable', label: 'Coffee table', source: 'primitive' },
  { kind: 'shelf', label: 'Shelf', source: 'primitive' },
  { kind: 'easel', label: 'Easel', source: 'primitive' },
  { kind: 'lamp', label: 'Lamp', source: 'primitive' },
  { kind: 'plant', label: 'Plant', source: 'primitive' },
  { kind: 'rug', label: 'Rug', source: 'primitive' },
]
